import React, { useEffect, useState } from 'react';
import { MIcon } from '../../../lib/micon';
import { apiFetch, zeigeApiFehler } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { LdSheet } from '../../../components/ui/LdSheet';
import type { ElternKontakt, Gespraech, GespraechVorlage } from './profil-api';
import { wtTm, heuteIso } from './format';
import { DiktatFeldKnopf, useFeldDiktat } from './DiktatFeldKnopf';
import { LdDatum, LdFeldReihe, LdUhrzeit } from '../../../components/ld/LdFenster';

export const ARTEN = [['elterngespraech', 'Elterngespräch'], ['schuelergespraech', 'Schülergespräch'], ['telefonat', 'Telefonat']] as const;
export const ANLAESSE = [['fehlzeiten', 'Fehlzeiten'], ['leistung', 'Leistung'], ['verhalten', 'Verhalten'], ['hausaufgaben', 'Hausaufgaben'], ['foerderung', 'Förderung'], ['sonstiges', 'Sonstiges']] as const;
export const ROLLE_TEXT: Record<string, string> = { mutter: 'Mutter', vater: 'Vater', grosseltern: 'Großeltern', betreuer: 'Betreuer/in', unbekannt: 'Elternteil' };
type Teilnehmer = { art: 'eltern' | 'schueler' | 'sonstige'; eltern_id?: number; name: string };
type Vereinbarung = { id?: number; text: string; wer: string; bis_datum: string; status: 'offen' | 'erledigt' };

const jetztLokal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const plusTage = (iso: string, n: number) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
// KI-Freitext → Anlass-Chips (nur sichere Stichwörter, der Rest bleibt im Freitext „anlass“)
const ANLASS_WORTE: [RegExp, string][] = [[/fehl|schwänz|abwesen/i, 'fehlzeiten'], [/leistung|note|klassenarbeit|lernen/i, 'leistung'], [/verhalt|stör|konflikt|streit/i, 'verhalten'], [/hausaufgab/i, 'hausaufgaben'], [/förder|nachhilfe|unterstütz/i, 'foerderung']];

/** Welle P1c: „Gespräch dokumentieren“ — Erzählen (Diktat, die KI füllt die Felder, du prüfst) oder Tippen */
export function GespraechSheet({ open, schuelerId, schuelerName, vorname, eltern, gespraech, vorlage = null, startText = null, onClose, onGespeichert }: {
  open: boolean; schuelerId: number; schuelerName: string; vorname: string; eltern: ElternKontakt[];
  gespraech: Gespraech | null; isMobile: boolean; onClose: () => void;
  /** Welle B3: das gespeicherte Gespräch (neu oder geändert) wird mitgegeben */
  onGespeichert: (g?: Gespraech) => void;
  /** Welle B3: aus dem Diktat — Felder über die vorhandene Strukturierung vorbelegen (wie „Erzählen“) */
  startText?: string | null;
  /** Welle B2: neues Gespräch aus einem bestätigten Eltern-Termin (Datum, Art, Eltern vorbelegt) */
  vorlage?: GespraechVorlage | null;
}) {
  const [modus, setModus] = useState<'erzaehlen' | 'tippen'>('tippen');
  const [datum, setDatum] = useState(jetztLokal());
  const [art, setArt] = useState<string>('elterngespraech');
  const [teilnehmer, setTeilnehmer] = useState<Teilnehmer[]>([]);
  const [weitererName, setWeitererName] = useState<string | null>(null);
  const [anlaesse, setAnlaesse] = useState<string[]>([]);
  const [anlass, setAnlass] = useState('');
  const [inhalt, setInhalt] = useState('');
  const [vereinbarungen, setVereinbarungen] = useState<Vereinbarung[]>([]);
  const [nachfassen, setNachfassen] = useState(false);
  const [nachfassenAm, setNachfassenAm] = useState(plusTage(heuteIso(), 14));
  const [vertraulich, setVertraulich] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [kiLaeuft, setKiLaeuft] = useState(false);

  useEffect(() => {
    if (!open) return;
    const g = gespraech;
    setModus('tippen');
    const v = g ? null : vorlage;
    setDatum(g ? (g.datum.length > 10 ? g.datum.slice(0, 16) : `${g.datum}T00:00`) : v ? v.datum : jetztLokal());
    setArt(g && ARTEN.some(([k]) => k === g.typ) ? g.typ : v && ARTEN.some(([k]) => k === v.art) ? v.art : 'elterngespraech');
    setTeilnehmer(g?.teilnehmer_liste ?? v?.teilnehmer ?? []);
    setWeitererName(null);
    setAnlaesse(g?.anlaesse ?? []);
    setAnlass(g?.anlass ?? '');
    setInhalt(g?.inhalt ?? '');
    setVereinbarungen((g?.vereinbarungen ?? []).map(v => ({ id: v.id, text: v.text, wer: v.wer ?? '', bis_datum: v.bis_datum ?? '', status: v.status })));
    setNachfassen(!!g?.nachfassen_am);
    setNachfassenAm(g?.nachfassen_am ?? plusTage(heuteIso(), 14));
    setVertraulich(g?.vertraulich === 1);
  }, [open, gespraech, vorlage]);

  const istGewaehlt = (t: Teilnehmer) => teilnehmer.some(x => (t.eltern_id ? x.eltern_id === t.eltern_id : x.art === t.art && x.name === t.name));
  const umschalten = (t: Teilnehmer) => setTeilnehmer(l => istGewaehlt(t) ? l.filter(x => !(t.eltern_id ? x.eltern_id === t.eltern_id : x.art === t.art && x.name === t.name)) : [...l, t]);
  const kind: Teilnehmer = { art: 'schueler', name: vorname };
  const elternChips: Teilnehmer[] = eltern.map(e => ({ art: 'eltern', eltern_id: e.id, name: e.name || ROLLE_TEXT[e.rolle] }));
  const sonstige = teilnehmer.filter(t => t.art === 'sonstige');

  // „Erzählen“: Aufnahme → Text → vorhandene KI-Strukturierung (Felder) → du prüfst im Modus „Tippen“
  // Welle B3: dieselbe Strukturierung startet auch mit einem Text aus dem Diktat (startText)
  const erzaehlen = useFeldDiktat((text) => ausText(text), () => setModus('tippen'));
  const startGenutzt = React.useRef<string | null>(null);
  useEffect(() => {
    if (!open) { startGenutzt.current = null; return; }
    if (startText && !gespraech && startGenutzt.current !== startText) { startGenutzt.current = startText; ausText(startText); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startText]);
  async function ausText(text: string) {
    setKiLaeuft(true);
    try {
      const d = await apiFetch<{ typ?: string; teilnehmer?: string; anlass?: string; inhalt?: string; vereinbarung?: string; follow_up_datum?: string | null }>(
        '/api/gespraeche/strukturiere', { method: 'POST', body: JSON.stringify({ transkript: text, schueler_name: schuelerName }) });
      if (d.typ && ARTEN.some(([k]) => k === d.typ)) setArt(d.typ);
      const tn = (d.teilnehmer ?? '').toLowerCase();
      const treffer: Teilnehmer[] = [];
      for (const e of eltern) {
        const name = (e.name ?? '').toLowerCase();
        if ((name && (tn.includes(name) || name.split(' ').some(teil => teil.length > 2 && tn.includes(teil)))) || tn.includes(ROLLE_TEXT[e.rolle].toLowerCase()))
          treffer.push({ art: 'eltern', eltern_id: e.id, name: e.name || ROLLE_TEXT[e.rolle] });
      }
      if (tn.includes(vorname.toLowerCase())) treffer.push(kind);
      if (treffer.length) setTeilnehmer(treffer);
      if (d.anlass) { setAnlass(d.anlass); setAnlaesse(ANLASS_WORTE.filter(([re]) => re.test(d.anlass!)).map(([, k]) => k)); }
      if (d.inhalt) setInhalt(d.inhalt);
      if (d.vereinbarung?.trim()) setVereinbarungen([{ text: d.vereinbarung.trim(), wer: '', bis_datum: '', status: 'offen' }]);
      if (d.follow_up_datum) { setNachfassen(true); setNachfassenAm(d.follow_up_datum); }
      toast('Die KI hat die Felder gefüllt – bitte prüfen.', 'success');
    } catch {
      setInhalt(i => (i ? `${i}\n${text}` : text));
      toast('Felder konnten nicht automatisch gefüllt werden – der Text steht unter „Was wurde besprochen?“.', 'info');
    } finally { setKiLaeuft(false); setModus('tippen'); }
  }

  async function speichern() {
    if (!datum) return;
    setLaeuft(true);
    const body = {
      art, datum, teilnehmer, anlaesse, anlass: anlass || null, inhalt: inhalt.trim() || null, vertraulich: vertraulich ? 1 : 0,
      vereinbarungen: vereinbarungen.filter(v => v.text.trim()).map(v => ({ ...(v.id ? { id: v.id } : {}), text: v.text.trim(), wer: v.wer.trim() || null, bis_datum: v.bis_datum || null, status: v.status })),
      nachfassen_am: nachfassen ? nachfassenAm : null,
    };
    try {
      const g = gespraech ? await apiFetch<Gespraech>(`/api/gespraeche/${gespraech.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : await apiFetch<Gespraech>('/api/gespraeche', { method: 'POST', body: JSON.stringify({ ...body, schueler_id: schuelerId, ...(vorlage ? { termin_id: vorlage.termin_id } : {}) }) });
      toast(nachfassen ? `Gespräch gespeichert · Nachfassen am ${wtTm(nachfassenAm)}` : 'Gespräch gespeichert', 'success');
      onGespeichert(g); onClose();
    } catch (err) { zeigeApiFehler(err, 'Gespräch konnte nicht gespeichert werden'); }
    finally { setLaeuft(false); }
  }

  return (
    <LdSheet open={open} onClose={onClose} maxWidth={620}
      kopf={{ titel: gespraech ? 'Gespräch bearbeiten' : 'Gespräch dokumentieren', kontext: `${schuelerName}${!gespraech && vorlage ? `, Termin vom ${wtTm(vorlage.datum.slice(0, 10))}` : ''}` }}>
      <div className="ld-gs">
        <div className="ld-gs__felder">
          {!gespraech && (
            <div className="ld-gs__modus" role="group" aria-label="Erfassen">
              <button type="button" aria-pressed={modus === 'erzaehlen'} className={modus === 'erzaehlen' ? 'an' : ''} onClick={() => setModus('erzaehlen')}>
                <MIcon name="mic" size={20} /><span><b>Erzählen</b><small>Die KI füllt die Felder, du prüfst</small></span></button>
              <button type="button" aria-pressed={modus === 'tippen'} className={modus === 'tippen' ? 'an' : ''} onClick={() => setModus('tippen')}>
                <MIcon name="keyboard" size={20} /><span><b>Tippen</b><small>Alles selbst eintragen</small></span></button>
            </div>
          )}
          {modus === 'erzaehlen' ? (
            <div className="ld-gs__erzaehlen">
              <p>Erzähl kurz, wer dabei war, worum es ging und was ihr vereinbart habt. Danach füllt die KI die Felder — du prüfst alles, bevor gespeichert wird.</p>
              <button type="button" className={`ld-gs__aufnahme${erzaehlen.zustand === 'aufnahme' ? ' an' : ''}`} disabled={erzaehlen.zustand === 'verarbeitet' || kiLaeuft}
                onClick={() => (erzaehlen.zustand === 'aufnahme' ? erzaehlen.stop() : erzaehlen.start())}>
                <MIcon name={erzaehlen.zustand === 'aufnahme' ? 'stop' : 'mic'} size={26} />
                {erzaehlen.zustand === 'aufnahme' ? 'Aufnahme beenden' : erzaehlen.zustand === 'verarbeitet' || kiLaeuft ? 'Wird verarbeitet …' : 'Aufnahme starten'}
              </button>
            </div>
          ) : (
            <>
              <div className="ld-gs__zeile2">
                <div className="ld-gs__feld"><span>Datum und Uhrzeit</span>
                  <LdFeldReihe>
                    <LdDatum aria-label="Datum" required value={datum.slice(0, 10)} onChange={e => setDatum(`${e.target.value}T${datum.slice(11, 16) || '12:00'}`)} />
                    <LdUhrzeit aria-label="Uhrzeit" placeholder="Uhrzeit" value={datum.slice(11, 16)} onChange={z => setDatum(`${datum.slice(0, 10)}T${z || '12:00'}`)} />
                  </LdFeldReihe></div>
                <div className="ld-gs__feld"><span>Art</span>
                  <div className="ld-gs__chips">{ARTEN.map(([k, l]) => <button key={k} type="button" aria-pressed={art === k} className={`ld-mws__chip${art === k ? ' an' : ''}`} onClick={() => setArt(k)}>{l}</button>)}</div></div>
              </div>
              <div className="ld-gs__feld"><span>Wer war dabei?</span>
                <div className="ld-gs__chips">
                  {elternChips.map(t => { const e = eltern.find(x => x.id === t.eltern_id)!; return (
                    <button key={`e${t.eltern_id}`} type="button" aria-pressed={istGewaehlt(t)} className={`ld-mws__chip${istGewaehlt(t) ? ' an' : ''}`} onClick={() => umschalten(t)}>
                      {istGewaehlt(t) && <MIcon name="check" size={15} />}{t.name} ({ROLLE_TEXT[e.rolle]})</button>); })}
                  <button type="button" aria-pressed={istGewaehlt(kind)} className={`ld-mws__chip${istGewaehlt(kind) ? ' an' : ''}`} onClick={() => umschalten(kind)}>
                    {istGewaehlt(kind) && <MIcon name="check" size={15} />}{vorname}</button>
                  {sonstige.map(t => (
                    <button key={`s${t.name}`} type="button" className="ld-mws__chip an" onClick={() => umschalten(t)} aria-label={`${t.name} entfernen`}><MIcon name="close" size={15} />{t.name}</button>))}
                  {weitererName === null
                    ? <button type="button" className="ld-mws__chip" onClick={() => setWeitererName('')}><MIcon name="add" size={15} />Weitere</button>
                    : <span className="ld-gs__weitere">
                        <input autoFocus value={weitererName} onChange={e => setWeitererName(e.target.value)} placeholder="Name, z. B. Herr Meier (Schulsozialarbeit)"
                          onKeyDown={e => { if (e.key === 'Enter' && weitererName.trim()) { e.preventDefault(); umschalten({ art: 'sonstige', name: weitererName.trim() }); setWeitererName(null); } }} />
                        <button type="button" className="ld-pu-link" disabled={!weitererName.trim()} onClick={() => { umschalten({ art: 'sonstige', name: weitererName.trim() }); setWeitererName(null); }}>Hinzufügen</button>
                      </span>}
                </div>
              </div>
              <div className="ld-gs__feld"><span>Anlass</span>
                <div className="ld-gs__chips">{ANLAESSE.map(([k, l]) => <button key={k} type="button" aria-pressed={anlaesse.includes(k)} className={`ld-mws__chip${anlaesse.includes(k) ? ' an' : ''}`}
                  onClick={() => setAnlaesse(a => a.includes(k) ? a.filter(x => x !== k) : [...a, k])}>{l}</button>)}</div>
              </div>
              <div className="ld-gs__feld"><span>Was wurde besprochen?</span>
                <div className="ld-gs__text">
                  <textarea rows={4} value={inhalt} onChange={e => setInhalt(e.target.value)} placeholder="Gesprächsverlauf, wichtige Aussagen …" aria-label="Was wurde besprochen?" />
                  <DiktatFeldKnopf onText={t => setInhalt(i => (i ? `${i} ${t}` : t))} label="Inhalt erzählen" />
                </div>
              </div>
              <div className="ld-gs__feld"><span>Vereinbarungen</span>
                {vereinbarungen.map((v, i) => (
                  <div key={i} className="ld-gs__vb">
                    <input value={v.text} onChange={e => setVereinbarungen(l => l.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} placeholder="Was gilt ab jetzt?" aria-label={`Vereinbarung ${i + 1}`} />
                    <div className="ld-gs__vb-zeile">
                      <input value={v.wer} onChange={e => setVereinbarungen(l => l.map((x, j) => j === i ? { ...x, wer: e.target.value } : x))} placeholder="Wer?" aria-label="Wer" />
                      <LdDatum value={v.bis_datum} onChange={e => setVereinbarungen(l => l.map((x, j) => j === i ? { ...x, bis_datum: e.target.value } : x))} aria-label="Bis wann" />
                      <button type="button" className="ld-pk-icon ld-pk-icon--klein" aria-label={`Vereinbarung ${i + 1} entfernen`} onClick={() => setVereinbarungen(l => l.filter((_, j) => j !== i))}><MIcon name="delete" size={16} /></button>
                    </div>
                  </div>
                ))}
                <button type="button" className="ld-pu-link" onClick={() => setVereinbarungen(l => [...l, { text: '', wer: '', bis_datum: '', status: 'offen' }])}><MIcon name="add" size={16} />Vereinbarung</button>
              </div>
              <div className="ld-gs__schalter">
                <MIcon name="event_repeat" size={20} />
                <div><b>Nachfassen{nachfassen ? ` am ${wtTm(nachfassenAm)}` : ''}</b><small>Erscheint dann in „Steht an“ und in Mein Tag</small>
                  {nachfassen && <LdDatum value={nachfassenAm} onChange={e => setNachfassenAm(e.target.value)} aria-label="Nachfassen am" />}</div>
                <button type="button" role="switch" aria-checked={nachfassen} aria-label="Nachfassen" className={`ld-gs__switch${nachfassen ? ' an' : ''}`} onClick={() => setNachfassen(n => !n)}><span /></button>
              </div>
              <div className="ld-gs__schalter">
                <MIcon name="lock" size={20} />
                <div><b>Vertraulich</b><small>Nur du siehst dieses Gespräch</small></div>
                <button type="button" role="switch" aria-checked={vertraulich} aria-label="Vertraulich" className={`ld-gs__switch${vertraulich ? ' an' : ''}`} onClick={() => setVertraulich(v => !v)}><span /></button>
              </div>
            </>
          )}
        </div>
        <div className="ld-gs__fuss">
          <button type="button" className="ld-mte__abbrechen" onClick={onClose}>Abbrechen</button>
          <button type="button" className="ld-mte__eintragen" onClick={speichern} disabled={laeuft || modus === 'erzaehlen'}><MIcon name="check" size={18} />{laeuft ? 'Speichern …' : 'Gespräch speichern'}</button>
        </div>
      </div>
    </LdSheet>
  );
}
